import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// ── Dropdown rendu via portail pour éviter tout problème de z-index / overflow ──
function UserDropdown({ anchorRef, onClose, user, initials, onNavigate, onLogout }) {
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [anchorRef]);

  // Fermer si clic en dehors du bouton ET en dehors du dropdown
  useEffect(() => {
    const handler = (e) => {
      const clickedInsideAnchor   = anchorRef.current?.contains(e.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(e.target);
      if (!clickedInsideAnchor && !clickedInsideDropdown) {
        onClose();
      }
    };
    // Utiliser 'click' (pas 'mousedown') pour laisser les onClick des boutons se déclencher d'abord
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [anchorRef, onClose]);

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: pos.top,
        right: pos.right,
        minWidth: 210,
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,.22)',
        border: '1px solid #e5e7eb',
        zIndex: 99999,
        overflow: 'hidden',
        animation: 'dropdownFadeIn .15s ease',
      }}
    >
      <style>{`
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Infos utilisateur */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        background: '#F8FAFC',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#1565C0', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}>{initials}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1a3a5c' }}>
              {user?.nom || user?.username}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
              {user?.username}
            </div>
            <div style={{
              display: 'inline-block', marginTop: 3,
              background: '#DBEAFE', color: '#1D4ED8',
              fontSize: 10, fontWeight: 600,
              padding: '1px 7px', borderRadius: 20,
            }}>
              {user?.role}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '6px 0' }}>
        <button
          onClick={onNavigate}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '9px 16px',
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: '#374151', textAlign: 'left',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
            stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
          Paramètres
        </button>

        <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />

        <button
          onClick={onLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '9px 16px',
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: '#DC2626', textAlign: 'left', fontWeight: 600,
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
            stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Déconnexion
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── Clé de persistance de l'état replié/déplié de la barre latérale ──
const SIDEBAR_COLLAPSED_KEY = 'gfm3_sidebar_collapsed';

// ── Layout principal ───────────────────────────────────────────
export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; }
    catch { return false; }
  });
  const btnRef = useRef(null);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); }
      catch { /* stockage indisponible — non bloquant */ }
      return next;
    });
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut();
    navigate('/login');
  };

  const handleNavigateSettings = () => {
    setMenuOpen(false);
    navigate('/parametres');
  };

  const initials = user?.nom
    ? user.nom.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : (user?.username ? user.username.slice(0, 2).toUpperCase() : 'AD');

  const isAdmin = ['direction', 'admin_rh', 'super_admin', 'rh'].includes(user?.role);

  return (
    <>
      {/* ── Styles du mode "barre latérale réduite" ──────────────────
          Injectés localement pour ne pas dépendre de styles.css :
          seules les règles nécessaires au mode réduit sont définies ici,
          le reste des styles (couleurs, polices, etc.) reste géré par
          la feuille de styles globale existante. */}
      <style>{`
        .sidebar { transition: width .18s ease, padding .18s ease; }
        .sidebar.collapsed { width: 64px; overflow: visible; }
        .sidebar.collapsed .nav-group-label,
        .sidebar.collapsed .nav-label,
        .sidebar.collapsed .role-box { display: none; }
        .sidebar.collapsed .nav-item {
          display: flex; justify-content: center; align-items: center;
          padding: 12px 0; gap: 0;
        }
        .sidebar.collapsed .nav-item svg { margin: 0; }
        .sidebar-toggle {
          display: flex; align-items: center; justify-content: center;
          width: 34px; height: 34px; border-radius: 8px;
          background: rgba(255,255,255,.08); border: none; cursor: pointer;
          color: #fff; flex-shrink: 0;
        }
        .sidebar-toggle:hover { background: rgba(255,255,255,.16); }
      `}</style>

      <header className="topbar">
        <div className="topbar-logo">
          <button
            className="sidebar-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Afficher le menu' : 'Réduire le menu'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Afficher le menu' : 'Réduire le menu'}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <img
            src="/logo_gfm3.png"
            alt="GFM3"
            style={{ height: 36, width: 'auto', objectFit: 'contain' }}
          />
          <span className="topbar-divider">|</span>
          <span className="topbar-title">Gestion des Ressources Humaines</span>
        </div>

        <div className="topbar-right">
          {/* Bouton utilisateur — ref sur ce bouton pour positionner le portail */}
          <div className="tb-user" ref={btnRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 8px', borderRadius: 8, color: '#fff',
              }}
            >
              <div className="tb-avatar">{initials}</div>
              <span style={{ fontSize: 13 }}>{user?.nom || user?.username}</span>
              <svg
                width="14" height="14" fill="#b0c8e8" viewBox="0 0 24 24"
                style={{
                  transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform .2s',
                }}
              >
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>

            {/* Dropdown via portail → s'affiche toujours au-dessus */}
            {menuOpen && (
              <UserDropdown
                anchorRef={btnRef}
                onClose={() => setMenuOpen(false)}
                user={user}
                initials={initials}
                onNavigate={handleNavigateSettings}
                onLogout={handleLogout}
              />
            )}
          </div>
        </div>
      </header>

      <div className="layout">
        <nav className={'sidebar' + (collapsed ? ' collapsed' : '')}>
          <div className="nav-group-label">Tableau de bord</div>
          <NavLink to="/" end title="Vue d'ensemble"
            className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
            <svg viewBox="0 0 24 24"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
            <span className="nav-label">Vue d'ensemble</span>
          </NavLink>

          <div className="nav-group-label">Gestion RH</div>
          {isAdmin && (
            <NavLink to="/employes" title="Fiche signalétique"
              className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
              <svg viewBox="0 0 24 24"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
              <span className="nav-label">Fiche signalétique</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/pointage" title="Pointage"
              className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
              <svg viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/><circle cx="12" cy="12" r="2"/></svg>
              <span className="nav-label">Pointage</span>
            </NavLink>
          )}
          <NavLink to="/conges" title="Congés & Absences"
            className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
            <svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>
            <span className="nav-label">Congés & Absences</span>
          </NavLink>
          <NavLink to="/temps" title="Feuille de temps"
            className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
            <svg viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/></svg>
            <span className="nav-label">Feuille de temps</span>
          </NavLink>

          {isAdmin && (
            <>
              <div className="nav-group-label">Administration</div>
              <NavLink to="/paie" title="Paie & Rémunérations"
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <svg viewBox="0 0 24 24"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
                <span className="nav-label">Paie & Rémunérations</span>
              </NavLink>
              <NavLink to="/sdtc" title="Solde de tout compte"
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                <span className="nav-label">Solde de tout compte</span>
              </NavLink>
              <NavLink to="/approbations" title="Approbations"
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                <span className="nav-label">Approbations</span>
              </NavLink>
              <NavLink to="/utilisateurs" title="Utilisateurs"
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <svg viewBox="0 0 24 24"><path d="M12 1C5.924 1 1 5.924 1 12s4.924 11 11 11 11-4.924 11-11S18.076 1 12 1zm0 4a3 3 0 110 6 3 3 0 010-6zm0 14.5c-2.757 0-5.197-1.34-6.747-3.413C6.22 14.42 9.02 13.5 12 13.5s5.78.92 6.747 2.587C17.197 18.16 14.757 19.5 12 19.5z"/></svg>
                <span className="nav-label">Utilisateurs</span>
              </NavLink>
              <NavLink to="/roles" title="Rôles & Droits"
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span className="nav-label">Rôles & Droits</span>
              </NavLink>
              <NavLink to="/parametres" title="Paramètres"
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                </svg>
                <span className="nav-label">Paramètres</span>
              </NavLink>
            </>
          )}

          <div className="role-box">
            <label>Profil actif</label>
            <select value={user?.role || 'lecteur'} readOnly onChange={() => {}}>
              <option value="direction">Direction</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin_rh">Admin RH</option>
              <option value="gestionnaire">Gestionnaire</option>
              <option value="lecteur">Lecteur</option>
            </select>
          </div>
        </nav>

        <main className="main"><Outlet /></main>
      </div>
    </>
  );
}
